import React from 'react';
import moment from 'moment-timezone';
import Box from 'components/Box';
import Row from 'components/Row';
import BoxBody from 'components/BoxBody';
import Divider from 'components/Divider';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemTitle,
} from 'components/Accordion';
import CoachingScheduleUpcomingInstance from './CoachingScheduleUpcomingInstance';
import CoachingSessionsHeader from './CoachingSessionsHeader';
import { sortInstancesDateMap, createInitialDateMap } from 'utils/coachingScheduleHelpers';
import { getWeeklyTimeframe } from 'coaching/lib/timeIntervals';

import 'coaching/components/styles/CoachingScheduleUpcomingSessions.scss';

import { GetCoachDashboardScheduleInstances_coaching_coachingSessionInstancesForCoach } from 'coaching/queries/__generated__/GetCoachDashboardScheduleInstances';

type Props = {
  weekOffset: number;
  instances?: GetCoachDashboardScheduleInstances_coaching_coachingSessionInstancesForCoach[];
};

export default function CoachingScheduleUpcomingSessions(props: Props) {
  const { instances = [], weekOffset } = props;
  const { startDateTime, endDateTime } = getWeeklyTimeframe(weekOffset);
  // Creating the initial date mapping based on the startDateTime and endDateTime
  //{ ['YYYY-MM-DD']: [] }
  const initialDateMap = createInitialDateMap(startDateTime, endDateTime);
  // Slotting the instances into their corresponding days in the generated date map
  // { ['YYYY-MM-DD']: CoachingSessionInstance[] }
  const dateMappingSorted = sortInstancesDateMap(initialDateMap, instances);
  const today = moment().endOf('day');
  return (
    <div className="CoachingScheduleUpcomingSessions">
      {Object.keys(dateMappingSorted).map(dateKey => {
        const instances = dateMappingSorted[dateKey];
        const instancesNotCancelled = instances.filter(
          instance => !instance.cancelled && !instance.cancelledLate
        );
        const dateIsBeforeToday = moment(dateKey).endOf('day').isBefore(today);
        return (
          <Row size="small" key={dateKey}>
            <Accordion accordion={true} data-test="coaching-schedule-accordion">
              <AccordionItem data-test="coaching-schedule-accordion-item" expanded={!dateIsBeforeToday && instances.length > 0}>
                <Box
                  className="CoachingScheduleUpcomingSessions-BoxTitle"
                  noShadow
                  textAlign="left"
                  bandHeaderSize="small"
                  bandHeader={
                    <AccordionItemTitle data-test="coaching-schedule-accordion-title" className="CoachingScheduleUpcomingSessions-AccordionItemTitle">
                      <CoachingSessionsHeader
                        date={dateKey}
                        numberOfSessions={instancesNotCancelled?.length}
                      />
                    </AccordionItemTitle>
                  }>
                  <AccordionItemBody data-test="coaching-schedule-accordion-item-body" className="CoachingScheduleUpcomingSessions-AccordionItemBody">
                    <BoxBody full flush divided={true}>
                      {instances.map(
                        (
                          instance: GetCoachDashboardScheduleInstances_coaching_coachingSessionInstancesForCoach,
                          index: number
                        ) => {
                          const isLastItem = index === instances.length - 1;
                          return (
                            <React.Fragment key={instance.uuid}>
                              <CoachingScheduleUpcomingInstance instance={instance} />
                              {!isLastItem && <Divider size="small" />}
                            </React.Fragment>
                          );
                        }
                      )}
                    </BoxBody>
                  </AccordionItemBody>
                </Box>
              </AccordionItem>
            </Accordion>
          </Row>
        );
      })}
    </div>
  );
}
